import { ObjectUtils } from 'Helpers';
import { Logger } from '../../../../Errors/Logger';
import { I18n } from '../../../../Locales/I18nService';
import { RoomInfos } from '../../../../Web/Pages/Quote/Components/Pannels/Rooms/Controllers/RoomInfosValues';
import { MapContextState } from '../../Context/MapContext';
import { IContournements } from '../../Models/Contournements';
import { YesOrNo } from '../../Models/IMapItem';
import { IOpening, OpeningType } from '../../Models/IOpening';
import { IRoom, RoomLocalisation, RoomShape, RoomType } from '../../Models/IRoom';
import { IRoomItem, RoomItemType } from '../../Models/IRoomItem';
import { OpeningUtils } from '../../Utils/Opening/OpeningUtils';
import { RoomUtils } from '../../Utils/Room/RoomUtils';
import { CoordPoint } from '../../Utils/Types';
import { fixRoomProperties } from './MapExportFixChanges';
import { ApiMapState, ExportMap } from './MapExportHelper';
import { MapHelper } from '../MapHelper';

type ExportMapV32Data = {
    rooms?: Partial<IExportRoom>[];
    openings?: Partial<IExportOpeningItem>[];
    roomItems?: Partial<IExportRoomItem>[];
};

type ExportMapV32 = ExportMap & { map?: ExportMapV32Data };

export class MapExportV32 {
    public static VERSION = 3.2;

    public static toApiJson = (state: MapContextState) => {
        const { openings, rooms, roomItems } = MapHelper.getRoomAllElements(state.rooms);
        const exportOpenings = OpeningUtils.exportOpenings(openings, rooms);
        const exportRooms = rooms?.map(RoomUtils.exportRoom);
        // const map_svg_preview_base64 = exporter.captureSvg();
        // const map_png_preview_base64 = await exporter.capturePng();
        const result: ExportMapV32 = {
            version: MapExportV32.VERSION,
            map: {
                rooms: exportRooms?.map(this.toExportRoom),
                openings: exportOpenings.map(this.toExportOpeningItem),
                roomItems: roomItems?.map(this.toExportRoomItem),
            },
        };
        const json = JSON.stringify(result);
        return json;
    };

    public static fromApiJsonToState = ({ map }: ExportMapV32, roomInfos: RoomInfos): ApiMapState => {
        const { rooms: exportRooms = [], openings: exportOpenings = [], roomItems: exportRoomItems = [] } = map || {};

        const rooms: IRoom[] = exportRooms.map((exportRoom: Partial<IExportRoom>) => {
            const room: IRoom = this.toRoom(exportRoom, roomInfos);
            room.openings = exportOpenings
                .filter(
                    (o: Partial<IExportOpeningItem>, index: number) =>
                        exportOpenings?.findLastIndex((x) => x.openingId === o.openingId) === index
                ) // filter duplicates
                .filter((o: Partial<IExportOpeningItem>) => o.roomId === room.roomId)
                .map(this.toOpeningItem);
            room.roomItems = exportRoomItems
                .filter(
                    (o: Partial<IExportRoomItem>, index: number) =>
                        exportRoomItems?.findLastIndex((x) => x.roomItemId === o.roomItemId) === index
                ) // filter duplicates
                .filter((i: Partial<IExportRoomItem>) => i.roomId === room.roomId)
                .map(this.toRoomItem);

            return room;
        });

        const exportMap = { state: { rooms } };
        Logger.log('MapExportV3.2 : fromApiJsonToState', exportMap);
        return exportMap;
    };

    //#region //* CONVERTER : MAP_ITEM TO EXPORT_ITEM

    public static toExportRoom = (baseRoom: IRoom): Partial<IExportRoom> => {
        const room = RoomUtils.exportRoom(baseRoom);
        return {
            roomId: room.roomId,
            name: room.name,
            namedByUser: room.namedByUser,
            shape: room.shape,
            type: room.type,
            roomWall: room.roomWall,
            // calcul_en_laize: room.calcul_en_laize, //todo @mmc
            // calcul_en_laize_sub_room_elements: room.calcul_en_laize_sub_room_elements,//todo @mmc
            //surface_area: room.surface_area

            //* USELESS
            //surface_area_without_room_item: room.surface_area_without_room_item,
            //perimeter: room.perimeter,
            //perimeter_plinthes: room.perimeter_plinthes,
            //raccord_length: room.raccord_length,
            //room_length: room.room_length,
            //surface_manipulee_prorata: room.surface_manipulee_prorata,
            //should_lay_plinthes_inside_room_element: room.should_lay_plinthes_inside_room_element,

            localisation: room.localisation,
            roomType: room.roomType,
            roomFloor: room.roomFloor,
            roomHeating: room.roomHeating,
            roomHeatingType: room.roomHeatingType,
            roomExistingFlooring: room.roomExistingFlooring,
            roomExistingFloorType: room.roomExistingFloorType,

            questionsCopiedFromId: room.questionsCopiedFromId,

            contournements: room.contournements,
            flooringWallStartValidated: room.flooringWallStartValidated,

            //* COORDS
            coords: room.coords,
            center: room.center,

            //sides: room.sides,
            //objects: room.objects, //todo @mmc, why is this here?
        };
    };

    public static toExportOpeningItem = (opening: IOpening): Partial<IExportOpeningItem> => {
        return {
            openingId: opening.openingId,
            roomId: opening.roomId,
            type: opening.type,
            name: opening.name,
            namedByUser: opening.namedByUser,

            size: opening.size,
            customSize: opening.customSize,

            location_side: opening.location_side,
            location_position_from_edge: opening.location_position_from_edge,

            x: opening.x,
            y: opening.y,
            limit: opening.limit, //! WHY
            angle: opening.angle, //! WHY

            //* USELESS
            // contiguous_between_rooms: opening.contiguous_between_rooms,

            // hinge: opening.hinge, //! WHY
            // scale: opening.scale,
            // thick: opening.thick,
            // value: opening.value,
            // width: opening.width,
            // height: opening.height,

            // position: opening.position,
            // angleSign: opening.angleSign,
        };
    };

    public static toExportRoomItem = (roomItem: IRoomItem): Partial<IExportRoomItem> => {
        return {
            type: roomItem.type,
            roomItemId: roomItem.roomItemId,
            roomId: roomItem.roomId,
            name: roomItem.name,
            namedByUser: roomItem.namedByUser,
            dimensId: roomItem.dimensId,

            putFlooring: roomItem.putFlooring,
            putPlinthes: roomItem.putPlinthes,
            isMoveableOutside: roomItem.isMoveableOutside,

            coordsReal: roomItem.coordsReal,

            //collisions: roomItem.collisions,
            //surface: roomItem.surface,
            //horizontal_size: roomItem.horizontal_size,
            //vertical_size: roomItem.vertical_size,
            //room_elements_length: roomItem.room_elements_length,
            //position: roomItem.position,
        };
    };

    //#endregion

    //#region //* CONVERTER : EXPORT_ITEM TO MAP_ITEM

    public static toRoom = (room: IExportRoom, roomInfos: RoomInfos): IRoom => {
        //const room = CreateRoomFactory.exportRoom(baseRoom);
        fixRoomProperties(room);
        if (room.roomHeatingType) {
            room.roomHeating =
                room.roomHeatingType === I18n.get('RoomHeatingTypeList_PasDeChauffage') ? YesOrNo.No : YesOrNo.Yes;
        }
        if (room.roomExistingFloorType) {
            const flooringTypeList =
                room.roomExistingFlooring === YesOrNo.Yes
                    ? roomInfos.getCurrentFloorWithFlooring(room.localisation!, room.type!)
                    : roomInfos.getCurrentFloorWithoutFlooring(room.localisation!, room.roomType!, room.type!);

            if (flooringTypeList.every((x) => x.value ? x.value !== room.roomExistingFloorType : x.label !== room.roomExistingFloorType)) {
                room.roomExistingFloorType = undefined;
            }
        }
        if (!room.type) {
            room.type = RoomType.Sol;
        }
        const coords = room.coords;
        const roomCoordsMerged = coords ? ObjectUtils.ignoreDuplicates(coords.slice(0, coords.length - 1)) : undefined;

        return {
            roomId: room.roomId,
            name: room.name,
            namedByUser: room.namedByUser,
            shape: room.shape,
            type: room.type,
            localisation: room.localisation,
            roomType: room.roomType,
            roomWall: room.roomWall,
            roomFloor: room.roomFloor,
            roomHeating: room.roomHeating,
            roomHeatingType: room.roomHeatingType,
            roomExistingFlooring: room.roomExistingFlooring,
            roomExistingFloorType: room.roomExistingFloorType,

            questionsCopiedFromId: room.questionsCopiedFromId,

            contournements: room.contournements,
            flooringWallStartValidated: room.flooringWallStartValidated,

            //* COORDS
            center: room.center,
            coords: roomCoordsMerged ? [...roomCoordsMerged, roomCoordsMerged[0]] : undefined,

            walls: [],
            openings: [],
            roomItems: [],

            //sides: room.sides,
            //objects: room.objects, //todo @mmc, why is this here?
            // calcul_en_laize: room.calcul_en_laize, //todo @mmc
            // calcul_en_laize_sub_room_elements: room.calcul_en_laize_sub_room_elements,//todo @mmc

            // surface_area: room.surface_area,
            // surface_area_without_room_item: room.surface_area_without_room_item,
            // perimeter: room.perimeter,
            // perimeter_plinthes: room.perimeter_plinthes,
            // raccord_length: room.raccord_length,
            // room_length: room.room_length,
            // surface_manipulee_prorata: room.surface_manipulee_prorata,
            // should_lay_plinthes_inside_room_element: room.should_lay_plinthes_inside_room_element,
        };
    };

    public static toOpeningItem = (opening: IExportOpeningItem): IOpening => {
        return {
            openingId: opening.openingId,
            roomId: opening.roomId,

            type: opening.type,
            name: opening.name,
            size: opening.size,
            customSize: opening.customSize,

            location_side: opening.location_side,
            location_position_from_edge: opening.location_position_from_edge,

            x: opening.x,
            y: opening.y,
            limit: opening.limit,
            angle: opening.angle,

            //* USELESS
            // contiguous_between_rooms: opening.contiguous_between_rooms, //!\ warning on load

            // hinge: opening.hinge,
            // scale: opening.scale,
            // thick: opening.thick,
            // value: opening.value,
            // position: opening.position,
            // angleSign: opening.angleSign,
            // width: opening.width,
            // height: opening.height,

            namedByUser: opening.namedByUser,
        };
    };

    public static toRoomItem = (roomItem: Partial<IExportRoomItem>): IRoomItem => {
        if (roomItem.dimensId === undefined) {
            roomItem.dimensId = 0;
        }
        return {
            type: roomItem.type,
            roomItemId: roomItem.roomItemId,
            roomId: roomItem.roomId,
            name: roomItem.name,
            namedByUser: roomItem.namedByUser,
            dimensId: roomItem.dimensId,

            putFlooring: roomItem.putFlooring,
            putPlinthes: roomItem.putPlinthes,
            isMoveableOutside: roomItem.isMoveableOutside,

            coordsReal: roomItem.coordsReal,

            //collisions: roomItem.collisions,
            // position: roomItem.position,
            // surface: roomItem.surface,
            // horizontal_size: roomItem.horizontal_size,
            // vertical_size: roomItem.vertical_size,
            // room_elements_length: roomItem.room_elements_length,
        };
    };

    //#endregion
}

//#region //* EXPORT MODELS

interface IExportRoom {
    shape?: RoomShape;
    roomId?: string;
    name?: string;
    namedByUser?: boolean;

    //* ROOM INFOS : injected in context
    roomType?: string; // rename
    localisation?: RoomLocalisation;
    roomFloor?: string;
    roomExistingFlooring?: YesOrNo | string; //presenceRevetementAuSol
    roomExistingFloorType?: string;
    roomHeating?: YesOrNo | string;
    roomHeatingType?: string;
    type?: RoomType;
    roomWall?: string;

    questionsCopiedFromId?: string;

    contournements?: IContournements;

    flooringWallStartValidated?: boolean;

    //* injected by MapResult -> calculate
    // raccord_length?: number;
    // room_length?: number;
    // surface_area?: number;
    // surface_area_without_room_item?: number;
    // surface_manipulee_prorata?: number;
    // perimeter?: number;
    // perimeter_plinthes?: number;
    // should_lay_plinthes_inside_room_element?: boolean;

    center?: CoordPoint;
    coords?: Array<CoordPoint>;
    //sides?: Map<string, Array<CoordPoint>>;
}

interface IExportOpeningItem {
    openingId?: string;
    roomId?: string;

    type?: OpeningType;
    name?: string;
    namedByUser?: boolean;

    size?: number;
    customSize?: boolean;

    //* OpeningProperties
    doorType?: string;
    unhingeableOpening?: string; //* Oui ou Non

    //* COORDS
    x?: number;
    y?: number;
    limit?: CoordPoint[];
    angle?: number;
    location_side?: string;
    location_position_from_edge?: number;

    // contiguous_between_rooms?: Array<string>; //! WHY
    // hinge?: string;
    // position?: CoordPoint;
    // thick?: number;
    // value?: number;
    // scale?: CoordPoint;
    // angleSign?: number;
    // width?: string;
    // height?: string;
}

interface IExportRoomItem {
    roomItemId?: string;
    roomId?: string;

    name?: string;
    namedByUser?: boolean;

    type: RoomItemType;
    putFlooring?: YesOrNo;
    putPlinthes?: string;
    isMoveableOutside?: boolean; // #20350
    dimensId?: number;

    width?: number;
    height?: number;

    coords?: Array<CoordPoint>;
    coordsReal?: Array<CoordPoint>;
    position?: CoordPoint;
    graphTranslation?: CoordPoint;

    angle?: number;

    //collisions?: Array<RoomCollision>;
    //injected in context
    //surface?: number;
    //horizontal_size?: number;
    //vertical_size?: number;
    //room_elements_length?: number;
}

//#endregion
